/**
 * Client-side error logger that saves errors to the database
 */

import { createClient } from '@repo/supabase/client';
import { createErrorTrackingService, type ErrorLogData } from '@repo/supabase/services';
import { AppError, ErrorHandler } from '@repo/errors';

// Fragment-stripping helper is shared via @repo/supabase — see
// packages/supabase/src/utils/safe-url.ts for the rationale (URL
// fragments carry live credentials on some flows). Local wrapper
// normalises null→undefined to fit the existing ErrorLogData shape.
import { safeUrlForLog as safeUrlForLogShared } from '@repo/supabase';
const safeUrlForLog = (): string | undefined =>
  safeUrlForLogShared() ?? undefined;

class ClientErrorLogger {
  private static instance: ClientErrorLogger;
  private errorTracker: ReturnType<typeof createErrorTrackingService> | null = null;
  private userId: string | null = null;
  private userRole: string | null = null;

  private constructor() {
    this.initializeErrorTracker();
  }

  static getInstance(): ClientErrorLogger {
    if (!ClientErrorLogger.instance) {
      ClientErrorLogger.instance = new ClientErrorLogger();
    }
    return ClientErrorLogger.instance;
  }

  private async initializeErrorTracker() {
    try {
      const supabase = createClient();
      this.errorTracker = createErrorTrackingService(supabase);
      
      // Get current user if available
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        this.userId = user.id;
        this.userRole = 'client';
      }

      // Listen for auth changes
      supabase.auth.onAuthStateChange((event, session) => {
        if (session?.user) {
          this.userId = session.user.id;
          this.userRole = 'client';
        } else {
          this.userId = null;
          this.userRole = null;
        }
      });
    } catch (error) {
      console.error('[ClientErrorLogger] Failed to initialize:', error);
    }
  }

  /**
   * Log an error to the database
   */
  async logError(error: unknown, context?: {
    componentName?: string;
    action?: string;
    metadata?: Record<string, any>;
  }): Promise<void> {
    try {
      // Normalize the error
      const appError = error instanceof AppError ? error : ErrorHandler.normalize(error);
      
      // Don't log to DB in development unless it's critical
      if (process.env.NODE_ENV === 'development' && appError.isOperational) {
        console.log('[ClientErrorLogger] Skipping DB log in dev for operational error');
        return;
      }

      // Prepare error data
      const errorData: ErrorLogData = {
        message: appError.message,
        errorStack: appError.stack,
        errorType: appError.code,
        severity: this.getSeverity(appError),
        userId: this.userId || undefined,
        userRole: this.userRole || undefined,
        userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        url: typeof window !== 'undefined' ? safeUrlForLog() : undefined,
        method: 'CLIENT',
        statusCode: appError.statusCode,
        appName: 'client',
        environment: process.env.NODE_ENV || 'production',
        componentName: context?.componentName,
        action: context?.action,
        metadata: {
          ...context?.metadata,
          errorContext: appError.context,
          category: ErrorHandler.getCategory(appError),
          isRetriable: ErrorHandler.isRetriable(appError)
        }
      };

      // Log to database
      if (this.errorTracker) {
        await this.errorTracker.logError(errorData);
        console.log('[ClientErrorLogger] Error logged to database');
      } else {
        console.warn('[ClientErrorLogger] Error tracker not initialized');
      }
    } catch (err) {
      // Never let error logging break the app
      console.error('[ClientErrorLogger] Failed to log error:', err);
    }
  }

  /**
   * Set user context for error logging
   */
  setUserContext(userId: string, userRole?: string) {
    this.userId = userId;
    this.userRole = userRole || 'client';
  }

  /**
   * Clear user context
   */
  clearUserContext() {
    this.userId = null;
    this.userRole = null;
  }

  /**
   * Log a warning to the database
   */
  async logWarning(message: string, context?: {
    componentName?: string;
    action?: string;
    metadata?: Record<string, any>;
  }): Promise<void> {
    try {
      const errorData: ErrorLogData = {
        message,
        severity: 'warn',
        userId: this.userId || undefined,
        userRole: this.userRole || undefined,
        userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        url: typeof window !== 'undefined' ? safeUrlForLog() : undefined,
        appName: 'client',
        environment: process.env.NODE_ENV || 'production',
        componentName: context?.componentName,
        action: context?.action,
        metadata: context?.metadata
      };

      // Log to database
      if (this.errorTracker) {
        await this.errorTracker.logError(errorData);
      }
    } catch (err) {
      console.error('[ClientErrorLogger] Failed to log warning:', err);
    }
  }

  /**
   * Get severity level based on error type
   */
  private getSeverity(error: AppError): 'debug' | 'info' | 'warn' | 'error' | 'critical' {
    if (!error.isOperational) {
      return 'critical';
    }

    switch (error.statusCode) {
      case 400:
      case 401:
      case 403:
      case 404:
        return 'warn';
      case 429:
        return 'info';
      case 500:
      case 502:
      case 503:
        return 'error';
      default:
        return error.statusCode >= 500 ? 'error' : 'warn';
    }
  }
}

// Export singleton instance
export const errorLogger = ClientErrorLogger.getInstance();

// Enhanced error handler that logs to database
export class EnhancedErrorHandler extends ErrorHandler {
  static async log(error: AppError, logger?: any, context?: any): Promise<void> {
    // Call parent log method (console logging)
    super.log(error, logger);
    
    // Also log to database
    await errorLogger.logError(error, context);
  }
}

// Export convenience function for logging errors
export async function logErrorToDatabase(
  error: unknown,
  componentName?: string,
  action?: string,
  metadata?: Record<string, any>
): Promise<void> {
  await errorLogger.logError(error, {
    componentName,
    action,
    metadata
  });
}